import React, { useState, useMemo } from 'react';
import { formatFirebaseTimestamp, formatMillisTimestamp } from '../../utils/datetime';
import MovementDetailModal from '../components/MovementDetailModal';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';

export default function Movements({ movements }) {
    const [detailMovement, setDetailMovement] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [sortColumn, setSortColumn] = useState(null); // 'updatedAt' | 'expiryTimestamp'
    const [sortOrder, setSortOrder] = useState('desc');

    const toggleSort = (column) => {
        if (sortColumn === column) {
            setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
        } else {
            setSortColumn(column);
            setSortOrder('desc');
        }
    };

    const filteredMovements = useMemo(() => {
        const query = searchQuery.toLowerCase().trim();
        const filtered = query
            ? movements.filter(m =>
                m?.id?.toLowerCase().includes(query) ||
                m?.exerciseType?.toLowerCase().includes(query) ||
                m?.host?.name?.toLowerCase().includes(query)
            )
            : movements;

        if (!sortColumn) return filtered;

        return [...filtered].sort((a, b) => {
            let valA, valB;
            if (sortColumn === 'updatedAt') {
                valA = a.updatedAt?.toDate?.()?.getTime() ?? 0;
                valB = b.updatedAt?.toDate?.()?.getTime() ?? 0;
            } else {
                valA = Number(a.expiryTimestamp) || 0;
                valB = Number(b.expiryTimestamp) || 0;
            }
            return sortOrder === 'asc' ? valA - valB : valB - valA;
        });
    }, [movements, searchQuery, sortColumn, sortOrder]);

    return (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div className="users-search-container">
                <SearchIcon sx={{ color: '#6c757d', fontSize: '20px' }} />
                <input
                    type="text"
                    placeholder="Search by ID, name, type or category..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="users-search-input"
                />
                {searchQuery && (
                    <ClearIcon
                        sx={{ color: '#adb5bd', fontSize: '18px', cursor: 'pointer', '&:hover': { color: '#6c757d' } }}
                        onClick={() => setSearchQuery('')}
                    />
                )}
            </div>

            <table className="request-table">
                <thead>
                    <tr>
                        <th style={{ width: '280px' }}>ID</th>
                        <th>Type</th>
                        <th>Created By</th>
                        <th>
                            <div className="sort-control" onClick={() => toggleSort('updatedAt')}>
                                <div className="sort-arrows">
                                    <ArrowDropUpIcon sx={{ color: sortColumn === 'updatedAt' && sortOrder === 'asc' ? 'var(--color-primary)' : '#adb5bd', fontSize: '18px', transform: 'translateY(4px)' }} />
                                    <ArrowDropDownIcon sx={{ color: sortColumn === 'updatedAt' && sortOrder === 'desc' ? 'var(--color-primary)' : '#adb5bd', fontSize: '18px', transform: 'translateY(-4px)' }} />
                                </div>
                                Updated
                            </div>
                        </th>
                        <th>
                            <div className="sort-control" onClick={() => toggleSort('expiryTimestamp')}>
                                <div className="sort-arrows">
                                    <ArrowDropUpIcon sx={{ color: sortColumn === 'expiryTimestamp' && sortOrder === 'asc' ? 'var(--color-primary)' : '#adb5bd', fontSize: '18px', transform: 'translateY(4px)' }} />
                                    <ArrowDropDownIcon sx={{ color: sortColumn === 'expiryTimestamp' && sortOrder === 'desc' ? 'var(--color-primary)' : '#adb5bd', fontSize: '18px', transform: 'translateY(-4px)' }} />
                                </div>
                                Expiry
                            </div>
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {filteredMovements.length === 0 ? (
                        <tr>
                            <td colSpan="5" className="empty-state">
                                {searchQuery ? 'No movements match your search' : 'No movements found'}
                            </td>
                        </tr>
                    ) : filteredMovements.map((movement, index) => (
                        <tr
                            key={index}
                            className="request-row"
                            style={{ cursor: 'pointer' }}
                            onClick={() => setDetailMovement(movement)}
                        >
                            <td style={{ fontFamily: 'monospace', fontSize: '12px', color: '#6c757d' }}>
                                {movement?.id}
                            </td>
                            <td>
                                {movement?.exerciseType ? (
                                    <span style={{
                                        padding: '4px 8px',
                                        backgroundColor: '#f0f0f0',
                                        borderRadius: '4px',
                                        fontSize: '12px',
                                        fontWeight: 500,
                                    }}>
                                        {movement.exerciseType}
                                    </span>
                                ) : '—'}
                            </td>
                            <td style={{ fontWeight: 500, color: '#1a1a2e' }}>
                                {movement?.host?.name || '—'}
                            </td>
                            <td style={{ color: '#6c757d', fontSize: '13px' }}>
                                {formatFirebaseTimestamp(movement?.updatedAt)}
                            </td>
                            <td style={{ color: '#6c757d', fontSize: '13px' }}>
                                {formatMillisTimestamp(movement?.expiryTimestamp)}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>

            {detailMovement && (
                <MovementDetailModal
                    movement={detailMovement}
                    onClose={() => setDetailMovement(null)}
                />
            )}
        </div>
    );
}
